<template>
  <div class="min-h-screen-safe bg-slate-900 font-sans text-slate-200 flex flex-col">
    
    <header class="flex-none sticky top-0 z-50 bg-[#0B1021]/95 backdrop-blur-md border-b border-slate-800 pt-safe flex flex-col shadow-lg">
      <div class="px-3 py-3 flex items-center justify-between relative">
        <NuxtLink :to="localePath('/')" class="flex items-center gap-1 text-slate-400 hover:text-white transition-colors group p-1 z-10">
          <svg class="w-6 h-6 transform group-hover:-translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          <span class="text-[14px] font-bold tracking-wider">{{ locale === 'zh' ? '返回首页' : 'Home' }}</span>
        </NuxtLink>

        <h1 class="absolute left-1/2 -translate-x-1/2 font-bold text-[15px] sm:text-[16px] text-white tracking-widest uppercase pointer-events-none w-max">
          {{ locale === 'zh' ? '关于 Lottobox' : 'About Lottobox' }}
        </h1>
        <div class="w-16"></div>
      </div>
    </header>

    <main class="flex-auto max-w-4xl mx-auto w-full p-6 pb-safe">
      <AboutZh v-if="locale === 'zh'" />
      <AboutEn v-else />
      <div class="h-12"></div>
    </main>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()

// 🚀 新增：引入路径智能转换引擎
const localePath = useLocalePath()

// 🚀 终极 SEO 双语矩阵：为 About 页面定制
const seoData = computed(() => {
  const isZh = locale.value === 'zh'
  return {
    title: isZh
      ? '关于我们 | 马新最快 4D 万字与马票开奖平台 - Lottobox'
      : 'About Us | Fastest Malaysia & Singapore 4D Results Platform - Lottobox',
    description: isZh
      ? '了解 Lottobox 如何通过毫秒级 API 直连技术，为您提供全网最快、无广告的万能、多多、大马彩及新加坡马会 4D 万字票实时开奖与现场摇珠直播服务。'
      : 'Learn how Lottobox provides the fastest, ad-free live 4D results and broadcast live draw for Magnum, Sports Toto, Damacai, and Singapore Pools.',
    keywords: '万字, 万字票, 万字成绩, 马票, 查马票, 马票平台, about Lottobox, 关于我们, Live 4D results, 4D实时开奖, Singapore Pools Broadcast live draw, Magnum 4D, Sports Toto, Damacai, GD Lotto, 4D FAQ'
  }
})

// 🚀 注入 Google 最爱的 FAQ 结构化数据
const faqSchema = computed(() => {
  const isZh = locale.value === 'zh'
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": isZh ? "Lottobox 万字与马票开奖大厅真的完全免费且无广告吗？" : "Is Lottobox Live really free and without ads?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": isZh 
            ? "是的！Lottobox Live 承诺永久免费向所有用户开放，并且页面绝无任何插屏广告、横幅广告干扰，为您提供最纯净的查万字与查马票体验。" 
            : "Absolutely! Lottobox Live is permanently free to use. We have strictly banned all pop-ups and banner ads to ensure you get the cleanest viewing experience."
        }
      },
      {
        "@type": "Question",
        "name": isZh ? "你们的万字开奖成绩有多快？" : "How fast are the 4D results updated?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": isZh 
            ? "我们通过独家 API 直连各官方机构。当官方现场摇出号码的 0.5 秒内，您的屏幕就会自动更新，完全不需要手动刷新页面。" 
            : "We auto-sync directly with official sources. The moment a number is drawn, your screen will update within 0.5 seconds automatically."
        }
      }
    ]
  }
})

useHead(() => ({
  title: seoData.value.title,
  meta: [
    { name: 'description', content: seoData.value.description },
    { name: 'keywords', content: seoData.value.keywords }
  ],
  script: [
    { type: 'application/ld+json', children: JSON.stringify(faqSchema.value) }
  ]
}))
</script>
